import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Free gift with purchase: free 1CT moissanite earrings with every moissanite tennis chain.
 *
 * - snippets/gift-offer-config.liquid lists the gift variant and the products that earn it.
 * - Whenever the cart changes: with a qualifying chain in the bag the gift is added once, marked with a "Gift" line
 *   property. A Shopify "Buy X get Y" discount makes it $0. Without a qualifying chain the gift is taken back out.
 * - Safety: a gift line that isn't actually free in the cart is taken straight back out, so nobody is ever charged
 *   for it. Only lines this script added count as the gift; a pair someone buys at full price is never touched.
 * - If the shopper removes the gift themselves, it is not forced back; the cart drawer offers an "Add" button instead
 *   (snippets/gift-offer-progress.liquid).
 */
const SKIP_KEY = 'st-gift-skip';
const HAD_KEY = 'st-gift-had';
const SOURCE = 'gift-offer';
const GIFT_PROPERTY = 'Gift';

const session = {
  get(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  },
  set(key, value) {
    try {
      if (value == null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch (error) {
      /* storage blocked: the gift simply follows the cart */
    }
  },
};

function readConfig() {
  const el = document.getElementById('gift-offer-config');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || '{}');
  } catch (error) {
    return null;
  }
}

const config = readConfig();
const triggers = new Set(config?.triggers || []);

async function getCart() {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

async function post(path, body) {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Gift offer: ${path} failed`);
  return response.json();
}

/** Re-render the cart drawer and bubble through the theme's own cart event, without popping the drawer open */
function announce(cart) {
  const drawer = document.querySelector('cart-drawer-component');
  const autoOpen = drawer?.hasAttribute('auto-open');
  if (autoOpen) drawer.removeAttribute('auto-open');
  document.dispatchEvent(new CartAddEvent(cart, SOURCE, { source: SOURCE, itemCount: cart.item_count }));
  if (autoOpen) drawer.setAttribute('auto-open', '');
}

const isGiftLine = (item) => item.variant_id === config.variant && Boolean(item.properties?.[GIFT_PROPERTY]);

function state(cart) {
  const giftLines = cart.items.filter(isGiftLine);
  const qualified = cart.items.some((item) => triggers.has(item.product_id));
  return { giftLines, qualified, hasGift: giftLines.length > 0 };
}

/** Small pop-up so shoppers see the gift even when the cart drawer stays closed after "Add to cart" */
let toastTimer;
function toast(message) {
  let el = document.getElementById('gift-toast');
  if (!el) {
    const style = document.createElement('style');
    style.textContent =
      '#gift-toast{position:fixed;left:50%;bottom:max(16px,env(safe-area-inset-bottom));z-index:1000;display:flex;align-items:center;gap:10px;' +
      'width:max-content;max-width:calc(100vw - 24px);box-sizing:border-box;padding:12px 16px;border:1px solid rgba(255,255,255,.3);border-radius:14px;background:#0b0b0d;color:#fff;' +
      'font-family:inherit;font-size:13px;font-weight:600;line-height:1.35;box-shadow:0 14px 40px rgba(0,0,0,.35);transform:translate(-50%,140%);opacity:0;cursor:pointer;' +
      'transition:transform .45s cubic-bezier(.22,1,.36,1),opacity .3s ease}' +
      '#gift-toast.is-shown{transform:translate(-50%,0);opacity:1}' +
      '#gift-toast b{flex:none;padding:6px 10px;border-radius:999px;background:#fff;color:#0b0b0d;font-size:10px;letter-spacing:.12em;text-transform:uppercase}' +
      '@media (prefers-reduced-motion:reduce){#gift-toast{transition:none}}';
    document.head.append(style);
    el = document.createElement('div');
    el.id = 'gift-toast';
    el.setAttribute('role', 'status');
    el.innerHTML = '<span></span><b>View bag</b>';
    el.addEventListener('click', () => {
      el.classList.remove('is-shown');
      document.querySelector('cart-drawer-component')?.showDialog?.();
    });
    document.body.append(el);
  }
  el.querySelector('span').textContent = message;
  requestAnimationFrame(() => el.classList.add('is-shown'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-shown'), 5000);
}

const drawerIsOpen = () => Boolean(document.querySelector('cart-drawer-component dialog')?.open);

let running = false;
let again = false;

async function reconcile() {
  if (running) {
    again = true;
    return;
  }
  running = true;
  let addedGift = false;
  try {
    let cart = await getCart();
    let changed = false;
    const { giftLines, qualified, hasGift } = state(cart);

    // The shopper took the gift out themselves: respect that until they ask for it again
    if (session.get(HAD_KEY) === cart.token && !hasGift && qualified) session.set(SKIP_KEY, cart.token);

    // Remove gifts that shouldn't be there, extra copies, and any gift that isn't actually free
    const updates = {};
    giftLines.forEach((line, index) => {
      if (!qualified || index > 0) updates[line.key] = 0;
      else if (line.final_line_price > 0 && line.quantity === 1) {
        updates[line.key] = 0;
        session.set(SKIP_KEY, cart.token);
      } else if (line.quantity > 1) updates[line.key] = 1;
    });
    if (Object.keys(updates).length) {
      await post('cart/update.js', { updates });
      changed = true;
    }

    if (qualified && !hasGift && session.get(SKIP_KEY) !== cart.token) {
      await post('cart/add.js', {
        items: [{ id: config.variant, quantity: 1, properties: { [GIFT_PROPERTY]: 'Complimentary with your tennis chain' } }],
      });
      changed = true;

      // Only keep it if the cart really makes it free
      const check = await getCart();
      const added = check.items.find(isGiftLine);
      if (added && added.final_line_price > 0) {
        await post('cart/change.js', { id: added.key, quantity: 0 });
        session.set(SKIP_KEY, check.token);
      } else if (added) {
        addedGift = true;
      }
    }

    if (changed) cart = await getCart();
    const now = state(cart);
    session.set(HAD_KEY, now.hasGift && now.qualified ? cart.token : null);

    if (changed) announce(cart);
    if (addedGift && !drawerIsOpen()) toast('🎁 Your FREE 1CT moissanite earrings were added to your bag');
  } catch (error) {
    /* network hiccup: the next cart change tries again */
  } finally {
    running = false;
    if (again) {
      again = false;
      reconcile();
    }
  }
}

if (config?.variant) {
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    reconcile();
  });

  // "Add free" in the cart drawer, after the shopper had removed it
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-gift-add]') : null;
    if (!button) return;
    event.preventDefault();
    session.set(SKIP_KEY, null);
    button.setAttribute('aria-busy', 'true');
    reconcile();
  });

  // Back/forward cache restores an old page: refresh against the real cart
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reconcile();
  });

  // Nothing to check on an empty cart, so most page loads make no extra request
  if (config.hasItems) reconcile();
}
